import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Spinner, Alert,
    InputGroup, Table, Badge
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";


export default function ModifierCommande() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        id_fournisseur: '',
        fournisseur_nom: '',
        date_commande: '',
        date_livraison_prevue: '',
        statut: 'brouillon',
        conditions_reglement: '',
        lignes: []
    });
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [statutOptions] = useState([
        { value: 'brouillon', label: 'Brouillon', icon: 'ri-draft-line', color: '#64748b' },
        { value: 'envoyee', label: 'Envoyer fournisseur', icon: 'ri-send-plane-line', color: '#3b82f6' },
        { value: 'annulee', label: 'Annuler', icon: 'ri-close-circle-line', color: '#ef4444' }
    ]);

    useEffect(() => {
        fetchCommande();
    }, [id]);

    const fetchCommande = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/commandes_fournisseurs.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setFormData({
                id_fournisseur: result.data.id_fournisseur,
                fournisseur_nom: result.data.fournisseur_nom,
                date_commande: result.data.date_commande,
                date_livraison_prevue: result.data.date_livraison_prevue || '',
                statut: result.data.statut || 'brouillon',
                conditions_reglement: result.data.conditions_reglement || '',
                lignes: result.data.lignes || []
            });
        } catch (err) {
            setSubmitError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleLigneChange = (index, field, value) => {
        const newLignes = [...formData.lignes];
        
        if (field === 'quantite_commandee') {
            newLignes[index][field] = parseInt(value) || 0;
        } else if (field === 'prix_unitaire_ht' || field === 'remise') {
            newLignes[index][field] = parseFloat(value) || 0;
        } else {
            newLignes[index][field] = value;
        }
        
        const ligne = newLignes[index];
        const quantite = parseFloat(ligne.quantite_commandee) || 0;
        const prix = parseFloat(ligne.prix_unitaire_ht) || 0;
        const remise = parseFloat(ligne.remise) || 0;
        
        ligne.montant_ligne_ht = quantite * prix * (1 - remise / 100);
        
        setFormData({ ...formData, lignes: newLignes });
    };

    const handleSupprimerLigne = (index) => {
        const newLignes = formData.lignes.filter((_, i) => i !== index);
        setFormData({ ...formData, lignes: newLignes });
    };

    const calculerTotaux = () => {
        const lignesCalculees = formData.lignes.map(ligne => ({
            ...ligne,
            quantite_commandee: parseFloat(ligne.quantite_commandee) || 0,
            prix_unitaire_ht: parseFloat(ligne.prix_unitaire_ht) || 0,
            remise: parseFloat(ligne.remise) || 0,
            tva_taux: parseFloat(ligne.tva_taux) || 18,
            css_taux: parseFloat(ligne.css_taux) || 1
        }));

        const montantHTTotal = lignesCalculees.reduce((acc, ligne) => {
            const montantLigne = ligne.quantite_commandee * ligne.prix_unitaire_ht * (1 - ligne.remise / 100);
            return acc + montantLigne;
        }, 0);

        const tvaTotal = lignesCalculees.reduce((acc, ligne) => {
            const montantLigne = ligne.quantite_commandee * ligne.prix_unitaire_ht * (1 - ligne.remise / 100);
            return acc + (montantLigne * ligne.tva_taux / 100);
        }, 0);

        const cssTotal = lignesCalculees.reduce((acc, ligne) => {
            const montantLigne = ligne.quantite_commandee * ligne.prix_unitaire_ht * (1 - ligne.remise / 100);
            return acc + (montantLigne * ligne.css_taux / 100);
        }, 0);

        return {
            montant_ht: montantHTTotal,
            montant_tva: tvaTotal,
            montant_css: cssTotal,
            montant_ttc: montantHTTotal + tvaTotal + cssTotal
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.lignes.length === 0) {
            setErrors({ lignes: "Ajoutez au moins un produit" });
            return;
        }

        setSaving(true);
        setSubmitError('');
        setSubmitSuccess(false);

        try {
            const token = localStorage.getItem("token");
            const totaux = calculerTotaux();

            const response = await fetch(
                `${API_BASE_URL}/commandes_fournisseurs.php?id=${id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        ...formData,
                        ...totaux
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setSubmitSuccess(true);
                setTimeout(() => {
                    navigate('/achats/commandes');
                }, 1500);
            } else {
                setSubmitError(result.message || "Erreur lors de la modification");
            }
        } catch (err) {
            setSubmitError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const totaux = calculerTotaux();

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement de la commande...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-pencil-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Modifier la commande</h4>
                                    <p className="text-muted small mb-0">{formData.fournisseur_nom}</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/achats/commandes')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Alertes */}
                    {submitSuccess && (
                        <Alert variant="success" onClose={() => setSubmitSuccess(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            Commande modifiée avec succès ! Redirection...
                        </Alert>
                    )}

                    {submitError && (
                        <Alert variant="danger" onClose={() => setSubmitError('')} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {submitError}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col lg={8}>
                                {/* Dates */}
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-calendar-line me-2"></i>
                                            Dates
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date commande</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="date_commande"
                                                        value={formData.date_commande}
                                                        onChange={handleChange}
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date livraison prévue</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="date_livraison_prevue"
                                                        value={formData.date_livraison_prevue}
                                                        onChange={handleChange}
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Statut */}
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-information-line me-2"></i>
                                            Statut de la commande
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Row className="g-3">
                                            {statutOptions.map(option => (
                                                <Col lg={4} key={option.value}>
                                                    <div
                                                        className={`statut-option ${formData.statut === option.value ? 'active' : ''}`}
                                                        onClick={() => setFormData({...formData, statut: option.value})}
                                                        style={{
                                                            padding: '1rem',
                                                            borderRadius: '12px',
                                                            border: `2px solid ${formData.statut === option.value ? option.color : '#e2e8f0'}`,
                                                            background: formData.statut === option.value ? `${option.color}10` : 'white',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className={option.icon} style={{ fontSize: '1.2rem', color: option.color }}></i>
                                                            <span className="fw-semibold flex-grow-1">{option.label}</span>
                                                            {formData.statut === option.value && (
                                                                <i className="ri-checkbox-circle-fill" style={{ color: option.color }}></i>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                        
                                        {/* Messages contextuels */}
                                        <div className="mt-3">
                                            {formData.statut === 'envoyee' && (
                                                <Alert variant="info" className="small p-3 mb-0 rounded-3" style={{ borderLeft: `3px solid #3b82f6` }}>
                                                    <i className="ri-send-plane-line me-2"></i>
                                                    La commande sera marquée comme envoyée au fournisseur.
                                                </Alert>
                                            )}
                                            {formData.statut === 'annulee' && (
                                                <Alert variant="danger" className="small p-3 mb-0 rounded-3" style={{ borderLeft: `3px solid #ef4444` }}>
                                                    <i className="ri-close-circle-line me-2"></i>
                                                    La commande sera annulée. Cette action est irréversible.
                                                </Alert>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>

                                {/* Produits */}
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-shopping-cart-line me-2"></i>
                                            Produits
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        {formData.lignes.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table className="align-middle mb-0 products-table">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th className="ps-4">Produit</th>
                                                            <th className="text-center">Qté</th>
                                                            <th className="text-end">Prix unit.</th>
                                                            <th className="text-center">Remise(%)</th>
                                                            <th className="text-end">Total HT</th>
                                                            <th className="text-center pe-4"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {formData.lignes.map((ligne, index) => (
                                                            <tr key={index} className="product-row">
                                                                <td className="ps-4">
                                                                    <div className="fw-semibold">{ligne.designation}</div>
                                                                    <small className="text-muted">{ligne.reference}</small>
                                                                </td>
                                                                <td className="text-center">
                                                                    <Form.Control
                                                                    size="sm" 
                                                                        type="number"
                                                                        min="1"
                                                                        value={ligne.quantite_commandee}
                                                                        onChange={(e) => handleLigneChange(index, 'quantite_commandee', parseInt(e.target.value) || 1)}
                                                                        className="text-center border-2 rounded-3"
                                                                        style={{ width: '80px', margin: '0 auto' }}
                                                                    />
                                                                </td>
                                                                <td className="text-end">
                                                                    <InputGroup size="sm" className="justify-content-end">
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="0"
                                                                            step="0.01"
                                                                            value={ligne.prix_unitaire_ht}
                                                                            onChange={(e) => handleLigneChange(index, 'prix_unitaire_ht', parseFloat(e.target.value) || 0)}
                                                                            className="border-2 rounded-3"
                                                                            style={{ width: '100px' }}
                                                                        />
                                                                          </InputGroup>
                                                                </td>
                                                                <td className="text-center">
                                                                    <InputGroup size="sm" className="justify-content-center">
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="0"
                                                                            max="100"
                                                                            value={ligne.remise || 0}
                                                                            onChange={(e) => handleLigneChange(index, 'remise', parseFloat(e.target.value) || 0)}
                                                                            className="text-center border-2 rounded-3"
                                                                            style={{ width: '70px' }}
                                                                        />
                                                                         </InputGroup>
                                                                </td>
                                                                <td className="text-end fw-bold text-primary">
                                                                    {formatCurrency(ligne.montant_ligne_ht || 0)}
                                                                </td>
                                                                <td className="text-center pe-4">
                                                                    <Button 
                                                                        variant="link" 
                                                                        className="text-danger p-0"
                                                                        onClick={() => handleSupprimerLigne(index)}
                                                                    >
                                                                        <i className="ri-delete-bin-line fs-16"></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="ri-shopping-cart-line fs-48 text-muted mb-3 d-block"></i>
                                                <p className="text-muted mb-3">Aucun produit</p>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col lg={4}>
                                <Card className="border-0 shadow-sm rounded-3 sticky-top" style={{ top: '20px' }}>
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-calculator-line me-2"></i>
                                            Résumé
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div className="recap-line d-flex justify-content-between mb-2">
                                            <span className="text-muted">Total HT</span>
                                            <span className="fw-bold">{formatCurrency(totaux.montant_ht || 0)}</span>
                                        </div>
                                        <div className="recap-line d-flex justify-content-between mb-2">
                                            <span className="text-muted">TVA (18%)</span>
                                            <span className="text-warning fw-semibold">{formatCurrency(totaux.montant_tva || 0)}</span>
                                        </div>
                                        <div className="recap-line d-flex justify-content-between mb-3">
                                            <span className="text-muted">CSS (1%)</span>
                                            <span className="text-info fw-semibold">{formatCurrency(totaux.montant_css || 0)}</span>
                                        </div>
                                        <hr />
                                        <div className="recap-line d-flex justify-content-between mb-4">
                                            <span className="fw-bold">Total TTC</span>
                                            <span className="fw-bold text-primary fs-4">{formatCurrency(totaux.montant_ttc || 0)}</span>
                                        </div>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold">Conditions de règlement</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                name="conditions_reglement"
                                                value={formData.conditions_reglement || ''}
                                                onChange={handleChange}
                                                placeholder="Ex: Paiement sous 30 jours..."
                                                className="border-2 rounded-3"
                                            />
                                        </Form.Group>

                                        <div className="d-grid gap-2">
                                            <Button 
                                                type="submit" 
                                                variant="primary" 
                                                className="rounded-pill py-2"
                                                disabled={saving}
                                                style={{ background: '#21ACCD', border: 'none' }}
                                            >
                                                {saving ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        Enregistrement...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ri-save-line me-2"></i>
                                                        Enregistrer
                                                    </>
                                                )}
                                            </Button>
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => navigate('/achats/commandes')}
                                                className="rounded-pill py-2"
                                            >
                                                Annuler
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>
                <Footer />
            </div>

            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-16 {
                    font-size: 1rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .statut-option {
                    transition: all 0.2s ease;
                }
                
                .statut-option:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }
                
                .recap-line {
                    padding: 0.5rem 0;
                }
                
                .products-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .products-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .product-row {
                    transition: all 0.2s ease;
                }
                
                .product-row:hover {
                    background: #f8fafc;
                }
            `}</style>
        </>
    );
}